import * as React from "react"
import type { ToastActionElement, ToastProps } from "@/components/ui/toast"

const TOAST_LIMIT = 5

// Grace period between a toast closing and leaving the array, long enough for
// the exit animation. The shadcn template ships 1000000 (~17 minutes), which
// keeps dead toasts in state for the rest of the session.
const TOAST_REMOVE_DELAY = 1000

// How long a toast stays up on its own. Radix has its own duration, but it
// pauses on window blur and only resumes on focus -- a browser that is visible
// while the keyboard focus sits in another app never dismisses anything, which
// is how the launch deep test saw toasts linger for minutes. This timer is
// wall-clock and focus-independent; hovering the viewport still pauses it.
const TOAST_AUTO_DISMISS_DELAY = 5000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

interface AutoDismiss {
  timeout: ReturnType<typeof setTimeout> | null
  remaining: number
  startedAt: number
}

const autoDismissTimers = new Map<string, AutoDismiss>()
let autoDismissPaused = false

const clearAutoDismiss = (toastId: string) => {
  const entry = autoDismissTimers.get(toastId)
  if (entry?.timeout) clearTimeout(entry.timeout)
  autoDismissTimers.delete(toastId)
}

const startAutoDismiss = (toastId: string, remaining: number) => {
  const entry: AutoDismiss = { timeout: null, remaining, startedAt: Date.now() }
  if (!autoDismissPaused) {
    entry.timeout = setTimeout(() => {
      autoDismissTimers.delete(toastId)
      dispatch({ type: "DISMISS_TOAST", toastId })
    }, remaining)
  }
  autoDismissTimers.set(toastId, entry)
}

/** Hold every countdown while the pointer rests on the toast viewport. */
export const pauseToastAutoDismiss = () => {
  if (autoDismissPaused) return
  autoDismissPaused = true
  const now = Date.now()
  autoDismissTimers.forEach((entry) => {
    if (entry.timeout) {
      clearTimeout(entry.timeout)
      entry.timeout = null
      entry.remaining = Math.max(0, entry.remaining - (now - entry.startedAt))
    }
  })
}

/** Resume every countdown from where it was paused. */
export const resumeToastAutoDismiss = () => {
  if (!autoDismissPaused) return
  autoDismissPaused = false
  Array.from(autoDismissTimers.entries()).forEach(([toastId, entry]) => {
    startAutoDismiss(toastId, entry.remaining)
  })
}

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        clearAutoDismiss(toastId)
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          clearAutoDismiss(toast.id)
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        state.toasts.forEach((t) => clearAutoDismiss(t.id))
        return {
          ...state,
          toasts: [],
        }
      }
      clearAutoDismiss(action.toastId)
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // duration: pass a number to override, or Infinity for a toast the user must
  // dismiss (a blocking error worth reading).
  const duration = props.duration ?? TOAST_AUTO_DISMISS_DELAY
  if (Number.isFinite(duration)) {
    startAutoDismiss(id, duration as number)
  }

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
